// Available UI element types configuration
export const UI_TYPES = [
  {
    id: 'iframe',
    name: 'iFrame',
    label: 'iFrame',
    title: 'iFrame',
    description: 'Embed external content using an iframe',
    icon: 'i-lucide-frame',
    itemType: 'visualization',
    category: 'UI Elements',
    schema: {
      url: { type: 'string', required: true },
    },
  },
  {
    id: 'link',
    name: 'Link',
    label: 'Link',
    title: 'Link',
    description: 'Display a clickable link with a URL',
    icon: 'i-lucide-mouse-pointer-click',
    itemType: 'visualization',
    category: 'UI Elements',
    schema: {
      url: { type: 'string', required: true },
    },
  },
  {
    id: 'ask_user',
    name: 'Ask User',
    label: 'Ask User',
    title: 'Ask User',
    description:
      'Present an inline form or multiple-choice question to collect structured input from the user',
    icon: 'i-lucide-message-square-text',
    itemType: 'visualization',
    category: 'UI Elements',
    toolName: 'ask_user',
    schema: {
      prompt: { type: 'string', required: true },
      fields: { type: 'array', required: true },
      button_label: { type: 'string', required: false },
    },
  },
  {
    id: 'open_tablefever',
    name: 'TableFever',
    label: 'TableFever',
    title: 'TableFever',
    description: 'Open the TableFever reservation widget for the guest',
    icon: 'i-lucide-utensils-crossed',
    itemType: 'visualization',
    category: 'UI Elements',
    toolName: 'open_tablefever',
    requiresIntegration: 'tablefever',
    schema: {
      title: { type: 'string', required: false },
      description: { type: 'string', required: false },
      date: { type: 'string', required: false },
      time: { type: 'string', required: false },
      event: { type: 'string', required: false },
    },
  },
  {
    id: 'open_sevenrooms',
    name: 'SevenRooms',
    label: 'SevenRooms',
    title: 'SevenRooms',
    description: 'Open the SevenRooms reservation widget for the guest',
    icon: 'i-lucide-calendar-check',
    itemType: 'visualization',
    category: 'UI Elements',
    toolName: 'open_sevenrooms',
    requiresIntegration: 'sevenrooms',
    schema: {
      title: { type: 'string', required: false },
      description: { type: 'string', required: false },
      date: { type: 'string', required: false },
      time: { type: 'string', required: false },
    },
  },
];

export const RESTAURANT_WIDGET_PROVIDERS = ['tablefever', 'sevenrooms'];

export const sanitizeRestaurantSlug = value =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '') || 'restaurant';

export const hookDisplayName = hook =>
  hook?.settings?.name ||
  hook?.settings?.venue_id ||
  hook?.settings?.resto_hash_id ||
  `Connection ${hook?.id}`;

export const getRestaurantHooksByProvider = getIntegration => {
  const result = {};
  RESTAURANT_WIDGET_PROVIDERS.forEach(id => {
    result[id] = getIntegration?.(id)?.hooks || [];
  });
  return result;
};

// Get UI element by id (static templates only; dynamic ids are built in getAllUi)
export const getUiById = id => {
  return UI_TYPES.find(ui => ui.id === id);
};

// Get all UI element types.
// Pass hooksByProvider `{ tablefever: [...hooks], sevenrooms: [...] }` to expand
// one visualization per connected restaurant widget. Arrays of connected app ids
// remain supported for backward compatibility.
export const getAllUi = (hooksByProvider = null) => {
  if (!hooksByProvider) {
    return [...UI_TYPES];
  }

  if (Array.isArray(hooksByProvider)) {
    const connected = new Set(hooksByProvider);
    return UI_TYPES.filter(
      ui => !ui.requiresIntegration || connected.has(ui.requiresIntegration)
    );
  }

  return UI_TYPES.flatMap(ui => {
    if (!ui.requiresIntegration) {
      return [ui];
    }

    const hooks = hooksByProvider[ui.requiresIntegration] || [];
    if (!hooks.length) {
      return [];
    }

    return hooks.map(hook => {
      const displayName = hookDisplayName(hook);
      const slug = hook.settings?.slug || sanitizeRestaurantSlug(displayName);
      const toolName = `${ui.toolName}_${slug}`;
      return {
        ...ui,
        id: toolName,
        name: `${ui.name}: ${displayName}`,
        label: displayName,
        title: displayName,
        description: `${ui.description} (${displayName})`,
        toolName,
      };
    });
  });
};
